import React, { type FunctionComponent, useMemo, useEffect } from 'react'
import { type FormikProps } from 'formik'
import { Flex } from 'legacy/dash-blocks/flex/Flex'
import { DashCard } from 'legacy/dash-blocks/dash-card/DashCard'
import { DashTextField } from 'legacy/dash-blocks/dash-text-field/DashTextField'
import { DashButton } from 'legacy/dash-blocks/dash-button/DashButton'
import { SimpleSelect } from 'legacy/dash-components/simple-select/SimpleSelect'
import { FormField } from 'legacy/dash-components/forms/form-field/FormField'
import { useDashIntl } from 'intl/intl.hooks'
import { type GeneratedReportsAddFormValues } from 'legacy/sections/generated-reports/components/add/components/form/utils/generatedReportsAddForm.types'
import { type UseGeneratedReportsAddBehaviour } from 'legacy/sections/generated-reports/components/add/utils/generatedReportsAdd.hooks'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { GeneratedReportsAddFormFilters } from 'legacy/sections/generated-reports/components/add/components/form/filters/components/GeneratedReportsAddFormFilters'
import {
  generatedReportsFilterInitialValueMapping,
  generatedReportsFilterFormKeyMapping,
} from 'legacy/sections/generated-reports/components/add/components/form/filters/utils/generatedReportsAddFormFilters.utils'

const REPORT_NAME_FIELD = 'reportName'
const REPORT_TYPE_FILED = 'reportType'

export interface GeneratedReportsAddFormProps
  extends Pick<UseGeneratedReportsAddBehaviour, 'datasetOptions' | 'filterOptions'>,
    FormikProps<GeneratedReportsAddFormValues> {}

export const GeneratedReportsAddForm: FunctionComponent<GeneratedReportsAddFormProps> = ({
  datasetOptions,
  ...rest
}) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    isValid,
    isSubmitting,
    handleSubmit,
    filterOptions,
  } = rest
  const intl = useDashIntl()

  const reportNamePlaceholder = intl.formatMessage({
    id: 'generatedReports.newReport.formFields.reportName.placeholder',
  })
  const reportTypePlaceholder = intl.formatMessage({
    id: 'generatedReports.newReport.formFields.reportType.placeholder',
  })

  const reportNameError = useMemo(() => {
    if (isSubmitting || !touched.reportName) return undefined

    return errors.reportName
  }, [errors.reportName, isSubmitting, touched.reportName])

  const reportTypeError = useMemo(() => {
    if (isSubmitting || !touched.reportType) return undefined

    return errors.reportType
  }, [errors.reportType, isSubmitting, touched.reportType])

  const handleChangeReportType = (value: string) => {
    setFieldValue(REPORT_TYPE_FILED, value)
  }
  const handleBlurReportType = () => {
    setFieldTouched(REPORT_TYPE_FILED)
  }

  const submitText = intl.formatMessage({ id: 'generatedReports.newReport.generateReport' })
  const handleDiscard = useGoToPath('generated-reports')
  const discardText = intl.formatMessage({ id: 'forms.discard' })

  const currentReportType = values.reportType

  const associatedReportFilters = useMemo(
    () => filterOptions[currentReportType] || [],
    [filterOptions, currentReportType]
  )

  // As a side effect of the currentReportType changing, we want to re-set all
  // the filter field values to their initial values
  useEffect(() => {
    if (associatedReportFilters) {
      associatedReportFilters.forEach(filter => {
        const key = generatedReportsFilterFormKeyMapping(filter)
        setFieldValue(key, generatedReportsFilterInitialValueMapping(filter))
      })
    }
  }, [filterOptions, currentReportType, associatedReportFilters, setFieldValue])

  return (
    <Flex fillWidth padding="medium">
      <DashCard fillWidth overrideStyle={{ overflow: 'visible' }}>
        <form onSubmit={handleSubmit}>
          <FormField
            name={REPORT_NAME_FIELD}
            intlId="generatedReports.newReport.formFields.reportName.label"
            error={reportNameError}
          >
            <DashTextField
              id={REPORT_NAME_FIELD}
              name={REPORT_NAME_FIELD}
              value={values.reportName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={reportNamePlaceholder}
              hasError={!!reportNameError}
              testId="generated-reports-add-form-name-field"
            />
          </FormField>

          <FormField
            name={REPORT_TYPE_FILED}
            intlId="generatedReports.newReport.formFields.reportType.label"
            error={reportTypeError}
          >
            <SimpleSelect
              openMenuOnFocus
              inputId={REPORT_TYPE_FILED}
              options={datasetOptions}
              value={values.reportType}
              placeholder={reportTypePlaceholder}
              onChange={handleChangeReportType}
              onBlur={handleBlurReportType}
              hasError={!!reportTypeError}
              testId="generated-reports-add-form-dataset-select"
            />
          </FormField>

          {currentReportType && (
            <GeneratedReportsAddFormFilters
              reportFilters={associatedReportFilters}
              reportType={currentReportType}
              {...rest}
            />
          )}

          <Flex justify="space-between" marginTop="small">
            <DashButton variant="outline-danger" onClick={handleDiscard}>
              {discardText}
            </DashButton>
            <DashButton
              variant="standard"
              type="submit"
              disabled={!isValid || isSubmitting}
              testId="generated-reports-add-form-submit"
            >
              {submitText}
            </DashButton>
          </Flex>
        </form>
      </DashCard>
    </Flex>
  )
}
